import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StatusPill from "@/components/common/StatusPill";
import { toast } from "sonner";
import { messaggioErrore } from "@/lib/preventivi";
import { Plus, Trash2 } from "lucide-react";
import { eur } from "@/lib/formato";
import { useConferma } from "@/hooks/use-conferma";


export default function FinanziamentoTab() {
  const { chiedi, dialogoConferma } = useConferma();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    importo_finanziato: "",
    durata_mesi: "",
    rata_mensile: "",
    taeg: "",
    ente: "",
    note: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/impostazioni/finanziamento/tabella");
      setRows(r.data || []);
    } catch (e) { toast.error(messaggioErrore(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        importo_finanziato: Number(form.importo_finanziato),
        durata_mesi: Number(form.durata_mesi),
        rata_mensile: Number(form.rata_mensile),
        taeg: Number(form.taeg),
        ente: form.ente || null,
        note: form.note || null,
        attivo: true,
      };
      await api.post("/impostazioni/finanziamento/tabella", body);
      setForm({ importo_finanziato: "", durata_mesi: "", rata_mensile: "", taeg: "", ente: "", note: "" });
      await load();
      toast.success("Riga aggiunta");
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    const ok = await chiedi({
      titolo: "Rimuovere questa riga di rate?",
      descrizione:
        "La combinazione importo · durata · rata sparisce dalle proponibili: i preventivi nuovi non potranno più offrirla.",
      verbo: "Rimuovi la riga",
    });
    if (!ok) return;
    try {
      await api.delete(`/impostazioni/finanziamento/tabella/${id}`);
      await load();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };

  return (
    <div className="space-y-6" data-testid="finanziamento-tab">
      {dialogoConferma}
      <Card className="p-card space-y-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Nuova riga rate</p>
          <p className="text-xs text-muted-foreground mt-1">
            Ogni riga corrisponde a una combinazione importo + durata. Il TAEG è obbligatorio: senza TAEG la riga non viene accettata dal database, e di conseguenza la durata non compare tra le proponibili.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="space-y-inside">
            <Label className="text-xs text-muted-foreground">Importo finanziato (€)</Label>
            <Input inputMode="decimal" value={form.importo_finanziato}
                   onChange={(e) => setForm({ ...form, importo_finanziato: e.target.value })}
                   placeholder="es. 10000" data-testid="fin-importo" />
          </div>
          <div className="space-y-inside">
            <Label className="text-xs text-muted-foreground">Durata (mesi)</Label>
            <Input type="number" min={1} value={form.durata_mesi}
                   onChange={(e) => setForm({ ...form, durata_mesi: e.target.value })}
                   placeholder="es. 24" data-testid="fin-durata" />
          </div>
          <div className="space-y-inside">
            <Label className="text-xs text-muted-foreground">Rata mensile (€)</Label>
            <Input inputMode="decimal" value={form.rata_mensile}
                   onChange={(e) => setForm({ ...form, rata_mensile: e.target.value })}
                   placeholder="es. 440,00" data-testid="fin-rata" />
          </div>
          <div className="space-y-inside">
            <Label className="text-xs text-muted-foreground">TAEG (%)</Label>
            <Input inputMode="decimal" value={form.taeg}
                   onChange={(e) => setForm({ ...form, taeg: e.target.value })}
                   placeholder="es. 7,5" data-testid="fin-taeg" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-inside">
            <Label className="text-xs text-muted-foreground">Ente</Label>
            <Input value={form.ente} onChange={(e) => setForm({ ...form, ente: e.target.value })}
                   placeholder="Findomestic, Compass, …" data-testid="fin-ente" />
          </div>
          <div className="space-y-inside">
            <Label className="text-xs text-muted-foreground">Note (facoltative)</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="fin-note" />
          </div>
        </div>
        <div>
          <Button
            onClick={submit}
            disabled={busy || !form.importo_finanziato || !form.durata_mesi || !form.rata_mensile || !form.taeg}
            data-testid="fin-submit"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Aggiungi riga
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="bg-muted px-4 py-2.5 border-b">
          <p className="text-eyebrow uppercase text-muted-foreground">
            Tabella caricata ({rows.length} righe)
          </p>
        </div>
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">
            Nessuna riga. Aggiungi le combinazioni importo/durata/rata/TAEG dal prospetto della finanziaria.
          </div>
        ) : (
          <div className="divide-y divide-border">
            <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-eyebrow uppercase text-muted-foreground bg-card">
              <div className="col-span-3">Importo</div>
              <div className="col-span-2">Durata</div>
              <div className="col-span-2">Rata</div>
              <div className="col-span-2">TAEG</div>
              <div className="col-span-2">Ente</div>
              <div className="col-span-1"></div>
            </div>
            {rows.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center text-sm"
                   data-testid={`fin-row-${r.id}`}>
                <div className="col-span-6 sm:col-span-3 tabular-nums">{eur(r.importo_finanziato)}</div>
                <div className="col-span-6 sm:col-span-2">{r.durata_mesi} mesi</div>
                <div className="col-span-6 sm:col-span-2 tabular-nums">{eur(r.rata_mensile)}</div>
                <div className="col-span-6 sm:col-span-2">
                  <StatusPill stato="ok" dot={false}>
                    {r.taeg}%
                  </StatusPill>
                </div>
                <div className="col-span-11 sm:col-span-2 text-muted-foreground text-xs">{r.ente || "—"}</div>
                <div className="col-span-1 flex justify-end">
                  <button onClick={() => remove(r.id)} aria-label="Togli la riga"
                          className="inline-flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-2 hover:text-danger-foreground"
                          data-testid={`fin-del-${r.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
